import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetTimezones } from "api/queries/common/useGetTimezones";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import { StopAddressType } from "types/MultiStop";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";
import {
  getStopTimezoneQueryPlan,
  isPortStopAddressType,
  mapTimezoneApiOptions,
  pickStopTimezoneOptions,
  resolveStopCountryCode,
  resolveStopTimezoneSelection,
  type StopTimezoneOption,
} from "@RequestFormV2/sections/Schedule/utils/stopTimezoneOptions";

const mapTimezoneSelect = (data: { value: string; name: string }[]) => mapTimezoneApiOptions(data);

export function useStopTimezoneOptions(stopIndex: number) {
  const { getValues, setValue } = useFormContext();

  const stopAddressType = useWatch({ name: getStopFieldPath(stopIndex, "stop_address_type") });
  const countryId = useWatch({ name: getStopFieldPath(stopIndex, "country_id") });
  const countryName = useWatch({ name: getStopFieldPath(stopIndex, "country_name") });
  const portCountry = useWatch({ name: getStopFieldPath(stopIndex, "port_country") });
  const stateName = useWatch({ name: getStopFieldPath(stopIndex, "state_name") });
  const addressName = useWatch({ name: getStopFieldPath(stopIndex, "address_name") });
  const addressReferenceNumber = useWatch({ name: getStopFieldPath(stopIndex, "address_reference_number") });

  const [pendingAddressTimezone, setPendingAddressTimezone] = useState<string | null | undefined>(undefined);
  const [portLinkedTimezone, setPortLinkedTimezone] = useState<string | null>(null);

  const { data: countries = [] } = useGetCountries({
    useV2: true,
    refetchOnMount: false,
  });

  const countryCode = useMemo(
    () => resolveStopCountryCode(countryId, countryName, portCountry, countries),
    [countries, countryId, countryName, portCountry],
  );

  const queryPlan = useMemo(
    () =>
      getStopTimezoneQueryPlan({
        addressType: stopAddressType,
        countryCode,
        stateName: stateName ? String(stateName) : undefined,
      }),
    [countryCode, stateName, stopAddressType],
  );

  const filteredRequestEnabled = queryPlan.isFiltered && Boolean(countryCode);
  const filteredQueryFilters = queryPlan.isFiltered ? queryPlan.filters : { __unfiltered: "1" };

  const {
    data: filteredOptions,
    isError: isFilteredError,
    isSuccess: isFilteredSuccess,
  } = useGetTimezones(filteredQueryFilters, {
    enabled: filteredRequestEnabled,
    select: mapTimezoneSelect,
  });

  const { data: fullListOptions } = useGetTimezones({}, {
    enabled: true,
    select: mapTimezoneSelect,
  });

  const filteredQueryFailed =
    filteredRequestEnabled && (isFilteredError || (isFilteredSuccess && (filteredOptions?.length ?? 0) === 0));

  const { options: timezones, isFiltered: effectiveIsFiltered } = useMemo(
    () =>
      pickStopTimezoneOptions({
        queryPlan,
        filteredOptions,
        filteredQueryFailed,
        fullListOptions,
      }),
    [filteredOptions, filteredQueryFailed, fullListOptions, queryPlan],
  );

  const isStreetAddress = Number(stopAddressType) === StopAddressType.ADDRESS;
  const isPortAddressType = isPortStopAddressType(stopAddressType);

  const { data: addressDetailsForTimezone } = useGetAddressesV2Details(String(addressReferenceNumber ?? ""), {
    options: {
      enabled: Boolean(isStreetAddress && addressReferenceNumber),
      refetchOnMount: true,
    },
  });

  const setAddressBookTimezone = useCallback((timezone: string | null | undefined) => {
    setPendingAddressTimezone(timezone ?? null);
  }, []);

  const setPortTimezone = useCallback((timezone: string | null | undefined) => {
    setPortLinkedTimezone(typeof timezone === "string" && timezone.trim() ? timezone.trim() : null);
    setPendingAddressTimezone(undefined);
  }, []);

  const addressBookTimezone = useMemo(() => {
    if (typeof pendingAddressTimezone === "string" && pendingAddressTimezone.trim()) {
      return pendingAddressTimezone;
    }
    if (pendingAddressTimezone === null) {
      return undefined;
    }
    const fromDetails = addressDetailsForTimezone?.timezone;
    if (typeof fromDetails === "string" && fromDetails.trim()) {
      return fromDetails;
    }
    return undefined;
  }, [addressDetailsForTimezone?.timezone, pendingAddressTimezone]);

  const hasAddressSource = Boolean(String(addressName ?? "").trim() || String(addressReferenceNumber ?? "").trim());

  useEffect(() => {
    if (!hasAddressSource) {
      setPendingAddressTimezone(undefined);
    }
  }, [hasAddressSource]);

  useEffect(() => {
    if (!isPortAddressType) {
      setPortLinkedTimezone(null);
    }
  }, [isPortAddressType]);

  useEffect(() => {
    if (!timezones.length) {
      return;
    }

    const timezonePath = getStopFieldPath(stopIndex, "stop_timezone");
    const current = getValues(timezonePath);
    const resolved = resolveStopTimezoneSelection({
      options: timezones,
      addressTimezone: addressBookTimezone,
      portTimezone: isPortAddressType ? portLinkedTimezone : null,
      currentTimezone: current,
      isFiltered: effectiveIsFiltered,
      hasAddressSource,
    });

    if (resolved === undefined || current === resolved) {
      return;
    }

    setValue(timezonePath, resolved, { shouldValidate: true });
  }, [
    addressBookTimezone,
    effectiveIsFiltered,
    getValues,
    hasAddressSource,
    isPortAddressType,
    portLinkedTimezone,
    setValue,
    stopIndex,
    timezones,
  ]);

  return {
    timezones: timezones as StopTimezoneOption[],
    setAddressBookTimezone,
    setPortTimezone,
  };
}

export type StopTimezoneOptionsSlice = ReturnType<typeof useStopTimezoneOptions>;
